import { useCallback, useEffect, useState } from "react";
import {
  acquireGraphToken,
  acquireGraphTokenSilent,
  signOut as signOutService,
} from "./authService";
import { getUsernameFromToken } from "./jwt";
import { getSilentFailureSummary, describeAuthError } from "./authDiagnostics";

export type AuthStatus = "checking" | "signedOut" | "signingIn" | "signedIn" | "error";

interface AuthState {
  status: AuthStatus;
  token?: string;
  username?: string;
  error?: string;
  /** Why the session could not be restored silently, shown on the sign-in screen. */
  diagnostic?: string;
}

function signedInState(token: string): AuthState {
  return { status: "signedIn", token, username: getUsernameFromToken(token) };
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({ status: "checking" });

  // On load, try to restore an existing session silently before prompting.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await acquireGraphTokenSilent();
        if (cancelled) return;
        setState(
          token
            ? signedInState(token)
            : { status: "signedOut", diagnostic: getSilentFailureSummary() }
        );
      } catch {
        if (!cancelled) {
          setState({ status: "signedOut", diagnostic: getSilentFailureSummary() });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async () => {
    setState({ status: "signingIn" });
    try {
      const token = await acquireGraphToken();
      setState(signedInState(token));
    } catch (err) {
      setState({ status: "error", error: describeAuthError(err) });
    }
  }, []);

  const signOut = useCallback(async () => {
    await signOutService();
    setState({ status: "signedOut" });
  }, []);

  return { ...state, signIn, signOut };
}
